import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import LeadFormBasicFields from "@/components/leadform/LeadFormBasicFields";
import LeadFormStandortFields from "@/components/leadform/LeadFormStandortFields";
import LeadFormSharedFields from "@/components/leadform/LeadFormSharedFields";
import LeadFormFileUpload from "@/components/leadform/LeadFormFileUpload";
import LeadFormFounderFields from "@/components/leadform/LeadFormFounderFields";

const INITIAL_STATE = {
  first_name: "",
  last_name: "",
  company: "",
  role: "",
  email: "",
  phone: "",
  website: "",
  has_location: "",
  location_type: "",
  city_region: "",
  available_area: "",
  ceiling_height: "",
  current_status: "",
  product_interest: "",
  target_group: "",
  timeline: "",
  message: "",
  consent: false,
  // Gründerpfad
  wish_region: "",
  operator_experience: "",
  founder_role: "",
  location_search_status: "",
  system_interests: "",
};

const HAS_LOCATION_OPTIONS = [
  { v: "yes", l: "Ja, ich habe eine Fläche" },
  { v: "no", l: "Nein, ich habe noch keine Fläche" },
];

export default function LeadForm({
  sourcePage = "standort-pruefen",
  preselectInterest,
  variant = "standort",
}) {
  const navigate = useNavigate();
  const [data, setData] = useState({
    ...INITIAL_STATE,
    product_interest: preselectInterest || "",
    has_location: variant === "founder" ? "no" : "",
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => setData((prev) => ({ ...prev, [key]: value }));

  const isFounder = data.has_location === "no";
  const isStandort = data.has_location === "yes";
  const pathChosen = isFounder || isStandort;

  const handleUpload = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      for (const file of picked) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/leads/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, res.data]);
      }
      toast.success("Datei(en) hochgeladen");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.consent) {
      toast.error("Bitte Datenschutzhinweis bestätigen");
      return;
    }
    if (!pathChosen) {
      toast.error("Bitte angeben, ob bereits eine Fläche vorhanden ist");
      return;
    }
    setSubmitting(true);
    try {
      // Strip path-irrelevant fields so the server gets a clean payload.
      const payload = { ...data, source_page: sourcePage };
      if (isFounder) {
        payload.location_type = "";
        payload.available_area = "";
        payload.ceiling_height = "";
        payload.current_status = "";
        payload.product_interest = "";
        payload.city_region = data.wish_region || "";
        payload.file_ids = [];
      } else {
        payload.wish_region = "";
        payload.operator_experience = "";
        payload.founder_role = "";
        payload.location_search_status = "";
        payload.system_interests = "";
        payload.file_ids = files.map((f) => f.id);
      }
      await api.post("/leads", payload);
      navigate("/danke");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Anfrage fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="surface p-6 md:p-10" data-testid="lead-form">
      <LeadFormBasicFields data={data} set={set} />

      <div className="hairline my-8" />

      <div data-testid="has-location-block">
        <label className="field-label">Haben Sie bereits eine konkrete Fläche? *</label>
        <select
          required
          value={data.has_location}
          onChange={(e) => set("has_location", e.target.value)}
          className="field"
          data-testid="select-has-location"
        >
          <option value="">Bitte wählen</option>
          {HAS_LOCATION_OPTIONS.map((o) => (
            <option key={o.v} value={o.v}>{o.l}</option>
          ))}
        </select>
      </div>

      {isStandort && (
        <>
          <div className="hairline my-8" />
          <div data-testid="standort-block">
            <div className="overline mb-4">Angaben zur Fläche</div>
            <LeadFormStandortFields data={data} set={set} />
            <div className="mt-5">
              <LeadFormSharedFields data={data} set={set} />
            </div>
          </div>
        </>
      )}

      {isFounder && (
        <>
          <div className="hairline my-8" />
          <LeadFormFounderFields data={data} set={set} />
          <div className="mt-6" data-testid="shared-block-founder">
            <LeadFormSharedFields data={data} set={set} />
          </div>
        </>
      )}

      {pathChosen && (
        <div className="mt-6">
          <label className="field-label">Nachricht</label>
          <textarea
            value={data.message}
            onChange={(e) => set("message", e.target.value)}
            className="field"
            placeholder={
              isFounder
                ? "Kurze Beschreibung Ihrer Gründungsidee oder Wunschregion."
                : "Kurze Beschreibung Ihres Standorts oder Ihrer Idee."
            }
            data-testid="input-message"
          />
        </div>
      )}

      {isStandort && (
        <LeadFormFileUpload
          files={files}
          uploading={uploading}
          onUpload={handleUpload}
          onRemove={removeFile}
        />
      )}

      <div className="hairline my-8" />

      <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-label">
        <input
          type="checkbox"
          checked={data.consent}
          onChange={(e) => set("consent", e.target.checked)}
          className="mt-1 accent-[#18E096] w-4 h-4"
          data-testid="input-consent"
        />
        <span className="text-sm text-[#AEB7B7] leading-relaxed">
          Ich bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage
          verarbeitet werden. Hinweise zum Datenschutz: meine Daten werden vertraulich
          behandelt und nicht an Dritte weitergegeben.
        </span>
      </label>

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary disabled:opacity-50"
          data-testid="lead-submit"
        >
          {submitting ? (
            <>
              <Loader2 size={16} strokeWidth={1.5} className="animate-spin" /> Wird gesendet…
            </>
          ) : (
            <>
              {isFounder ? "Gründer-Erstgespräch anfragen" : "Standortpotenzial prüfen"}{" "}
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
